import { IntlShape } from "react-intl";

// Shared layout constants and formatters for the Cockpit charts. Colours come
// from the theme (`theme.vars.palette.chart.*`, `seriesColor`).

// Recharts margins of the analyse charts. `CHART_MARGIN` (left Y axis): a
// negative left reclaims the gutter Recharts reserves for the axis (ticks are
// short), keeping the plot area wide in the 3-up layout. `RIGHT_AXIS_CHART_MARGIN`
// (right Y axis): no left gutter to cancel, both sides symmetric.
export const CHART_MARGIN = { top: 8, right: 8, left: -18, bottom: 0 };
export const RIGHT_AXIS_CHART_MARGIN = {
  ...CHART_MARGIN,
  left: CHART_MARGIN.right,
};

export const CHART_HEIGHT = 260;

// Taller plot for the Analyse-tab tiles: with their header + legend they land
// around the same footprint as the capped tables (MAX_TABLE_HEIGHT), keeping
// the tabs visually iso. The full-width sales-state chart keeps CHART_HEIGHT.
export const ANALYSE_CHART_HEIGHT = 450;

// Recharts tooltips are absolutely positioned with no z-index, so one that
// overflows its card is painted under the next card's chart (the recharts
// wrapper is `position: relative`). Lift every cockpit tooltip above.
export const TOOLTIP_WRAPPER_STYLE = { zIndex: 1 } as const;

// Shared legend spacing under the analyse / sales-state charts (module-level so
// the memoized ChartLegend keeps a stable `sx` reference).
export const LEGEND_SX = { mt: 1 } as const;

// Recharts' default `barCategoryGap`: a bar is the band minus this share on
// each side. Charts pass it explicitly so overlays drawn from the band scale
// hug the bars.
export const BAR_CATEGORY_GAP = 0.1;
export const barCategoryGapPercent = `${BAR_CATEGORY_GAP * 100}%`;
export const barGeometry = (bandStart: number, bandwidth: number) => {
  const gap = bandwidth * BAR_CATEGORY_GAP;
  return { left: bandStart + gap, right: bandStart + bandwidth - gap };
};

// Axis tick font size shared by the analyse charts (compact, to keep the
// 3-up layout readable without crowding).
export const AXIS_TICK_FONT_SIZE = 10;
// SVG captions drawn next to the active dots ("<series>: <value>").
export const CAPTION_FONT_SIZE = 11;

// Recharts' built-in XAxis height (its default when `height` is unset), and the
// taller axis the individual segmentation view needs for angled category labels.
export const RECHARTS_DEFAULT_X_AXIS_HEIGHT = 30;
export const ANGLED_X_AXIS_HEIGHT = 72;
// Lead-time charts: an X axis shorter than recharts' default so the hover badge
// band reserved beneath it (lead time + date on two lines, see
// LeadTimeAxisBadge) sits close under the tick labels.
export const LEAD_TIME_X_AXIS_HEIGHT = 22;
export const LEAD_TIME_BADGE_HEIGHT = 28;

// Reference-line labels (capacity) drawn inside the plot.
export const REFERENCE_LABEL_FONT_SIZE = 10;
// Rounded top corners of the bars.
export const BAR_RADIUS: [number, number, number, number] = [3, 3, 0, 0];

// Lead-time label (days before occupation, e.g. "J-120" / "D-120").
export const formatLeadTime = (intl: IntlShape, lt: number) =>
  intl.formatMessage({ id: "COCKPIT_LEAD_TIME_DAY" }, { days: lt });
